import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { parseDateOnly } from '@/lib/formatters'
import { type Driver, formatDriverDates } from '@/models/driver'
import type { Equipment } from '@/models/equipment'
import { type PolicyChangeInput, PolicyChangeKind, type PolicyChangeLine } from '@/models/policyChange'
import { DescribedChangeForm } from './DescribedChangeForm'
import { DriverModal } from './DriverModal'
import { EditCorrection } from './EditCorrection'
import { EditRemoval } from './EditRemoval'
import { changedFields, DRIVER_FIELDS, VEHICLE_FIELDS } from './EditUnitForm'
import { KIND_LABELS, toIsoDate } from './policyChangeCopy'
import { saved } from './saveOrToast'
import { VehicleModal } from './VehicleModal'

const Kind = PolicyChangeKind
const REMOVALS = [Kind.REMOVE_TRUCK, Kind.REMOVE_TRAILER, Kind.REMOVE_DRIVER]

export interface EditChangeProps {
  line: PolicyChangeLine
  trucks: Equipment[]
  trailers: Equipment[]
  drivers: Driver[]
  coverages: string[]
  /** Rejects with the API's error, which the roster's forms show on their fields. */
  onSave: (change: PolicyChangeInput) => Promise<void>
  onClose: () => void
}

/** A saved change opened in the form it was added with, filled in, so a correction needn't start over. */
export function EditChange({ line, trucks, trailers, drivers, coverages, onSave, onClose }: EditChangeProps) {
  const unitId = line.unit?.id
  // A change saved before the add form kept these has none.
  const answers: Partial<Record<string, string>> = line.details
  if (line.kind === Kind.ADD_TRUCK || line.kind === Kind.ADD_TRAILER) {
    const vehicles = line.kind === Kind.ADD_TRUCK ? trucks : trailers
    const vehicle = vehicles.find((candidate) => candidate.id === unitId)
    if (!vehicle) return null
    return (
      <VehicleModal
        kind={line.kind}
        item={vehicle}
        isNew={false}
        existing={vehicles.filter((other) => other.id !== unitId)}
        initial={{
          lossPayee: answers.lossPayee ?? '',
          garagingAddress: answers.garagingAddress ?? '',
          activeDate: parseDateOnly(line.startDate),
        }}
        onClose={onClose}
        onSubmit={(edited, extras) =>
          onSave({
            unit: { ...changedFields(vehicle, edited, VEHICLE_FIELDS) },
            details: {
              ...line.details,
              lossPayee: extras.lossPayee,
              garagingAddress: extras.garagingAddress,
            },
            startDate: toIsoDate(extras.activeDate),
          })
        }
      />
    )
  }
  if (line.kind === Kind.ADD_DRIVER) {
    const driver = drivers.find((candidate) => candidate.id === unitId)
    if (!driver) return null
    return (
      <DriverModal
        driver={driver}
        isNew={false}
        existing={drivers.filter((other) => other.id !== unitId)}
        initialFirstDrive={parseDateOnly(line.startDate)}
        onClose={onClose}
        onSubmit={(edited, firstDrive) =>
          onSave({
            unit: { ...formatDriverDates(changedFields(driver, edited, DRIVER_FIELDS)) },
            startDate: toIsoDate(firstDrive),
          })
        }
      />
    )
  }
  if (line.kind === Kind.CHANGE_VEHICLE || line.kind === Kind.CHANGE_DRIVER) {
    return (
      <EditCorrection
        line={line}
        trucks={trucks}
        trailers={trailers}
        drivers={drivers}
        onSave={onSave}
        onClose={onClose}
      />
    )
  }
  if (REMOVALS.includes(line.kind)) return <EditRemoval line={line} onSave={onSave} onClose={onClose} />
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{KIND_LABELS[line.kind]}</DialogTitle>
        </DialogHeader>
        <DescribedChangeForm
          kind={line.kind}
          coverages={coverages}
          initial={{ details: line.details, description: line.description }}
          submitLabel="Save change"
          onAdd={(change) => saved(onSave({ details: change.details, description: change.description }))}
        />
      </DialogContent>
    </Dialog>
  )
}
